import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
import type { Trip } from "@tdelivr/api-client/generated/trip/trip.js";
import { classOfStatus } from "@tdelivr/design-tokens";

import { absent, at, field, type RecordDetail } from "./record-detail.ts";

/**
 * What the active-rides board does to the rows TRP-01 returns, away from any rendering.
 *
 * The same split as `demand-queue.ts`, `offer-monitor.ts` and `live-map.ts`, for the same reason:
 * searching, ordering, paging and deciding what word a status gets can all be stated and tested
 * without a browser, and a component that does them inline can only be checked by rendering it.
 *
 * **This screen reads one listing and that is the whole of it.** TRP-01 answers
 * `GET /v1/tenants/{tenantId}/trips` — the operation whose own description calls itself "the
 * dispatch console's Active rides board" — and every column this screen can draw is on the row it
 * returns. So there is no aggregate: the console's own transport calls the service, as the demand
 * queue and the offer monitor do. Its delivery twin cannot, and
 * `services/experience/dispatch-console-bff` holds the reason.
 *
 * What is absent is most of what the design draws, and R-19 in
 * `docs/design/DESIGN_RECONCILIATION.md` is the measurement for each: three of its four tiles, its
 * third column, the rider's name, and the per-row detail panel's map and route.
 */

/** TRP-01's published status enum, as one name. */
export type TripStatus = Trip["status"];

/**
 * The five statuses TRP-01 treats as still running.
 *
 * The service's own live set, which its listing applies whenever no `status` is given — so the
 * board's unfiltered page is every trip in progress and not every trip the tenant has ever had.
 * Restated here rather than imported, because a console must not import a service's source;
 * asserted against the published enum in `tests/active-rides.test.ts` so it cannot drift silently.
 */
export const LIVE_STATUSES: readonly TripStatus[] = [
  "assigned",
  "driver_en_route",
  "driver_arrived",
  "waiting",
  "in_trip",
];

/**
 * Every status the listing's `status` filter accepts, in the contract's own order.
 *
 * Eight, because the filter goes to the service rather than over the rows the browser holds. The
 * design builds its filter from the states present in its four sample rows, and over a real listing
 * that is the trap the offer monitor already walked into: the unfiltered page is live-only, so a
 * filter built from it would offer the five running states and let an operator conclude that no
 * trip was ever completed. The three terminal statuses are reachable only by asking TRP-01 for one
 * by name.
 */
export const FILTERABLE_STATUSES: readonly TripStatus[] = [
  "assigned",
  "driver_en_route",
  "driver_arrived",
  "waiting",
  "in_trip",
  "completed",
  "cancelled",
  "no_show",
];

/**
 * TRP-01's vocabulary in the design's words.
 *
 * Two of the eight are written on the design's own sample rows — `in trip` and `completed` — so
 * those are taken verbatim. A third is written as **`cancelled by rider`**, and the half of it this
 * screen cannot say is `by rider`: TRP-01 publishes `reasonCode` on a *transition* and nothing on
 * the trip row records who cancelled it, so the word here is `cancelled` and the attribution is
 * absent rather than guessed. R-19.
 *
 * The design's fourth badge word, `review`, is not a status at all. It sits on a row the design
 * classes `human` and labels a fare dispute, and no trip status anywhere on this platform is
 * disputed — the same absence R-09 found when design rule 5's purple route colour had nothing to
 * come from. So no row on this board can ever read `review`.
 *
 * The remaining four are drawn nowhere in the bundle and take the plainest true word each. The
 * badge beside them carries the class, so the word only has to name the fact — and the four are
 * genuinely four different facts rather than one: a driver who has not set off, one on the way, one
 * at the kerb and one waiting for a rider who has not come out are four different things for a
 * dispatcher to do.
 */
export function readableStatus(status: TripStatus): string {
  switch (status) {
    case "assigned":
      return "assigned";
    case "driver_en_route":
      return "driver en route";
    case "driver_arrived":
      return "driver arrived";
    case "waiting":
      return "waiting";
    case "in_trip":
      return "in trip";
    case "completed":
      return "completed";
    case "cancelled":
      return "cancelled";
    case "no_show":
      return "no show";
  }
}

/**
 * The design's `Rider` cell: the identifier, because there is no name to put beside it.
 *
 * The design writes `Amina K.` on three rows and `User 4419` on the fourth, so it already expects
 * the name to be missing sometimes. On this platform it is missing always. Measured across every
 * operation a console can call:
 *
 *   - TRP-01 publishes `riderId` and nothing else about the rider.
 *   - IAM-01's `GET /v1/customer-identities/{identityId}` publishes `email`, `status` and three
 *     versions — no display name — under `identity:read`, which `navigation.ts` does not grant this
 *     page, and nothing states that a `riderId` is an `identityId`.
 *   - CUS-01's customer profile is the obvious home for a name and answers no listing and no read
 *     this console can reach; CUS-02 and CUS-03 answer saved places and preferences.
 *
 * So this is not the offer monitor's `M. Achieng · drv_4410`, where the name half exists behind a
 * scope the page may not hold. There is no name half. The cell is the identifier, the column stays
 * because it has a source, and R-19 records what would have to be published for it to say a name.
 */
export function riderCell(trip: Trip): string {
  return trip.riderId;
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type RideOrder = "default" | "state" | "az" | "za";

export interface RideQuery {
  /** Free text over every value on the row, as the design's grid searches. */
  readonly search: string;
  readonly order: RideOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
}

export interface RidePage {
  /** The rows on the current page. */
  readonly rows: readonly Trip[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly Trip[];
  readonly total: number;
  readonly pageCount: number;
}

/**
 * Every word on a row that a search should match.
 *
 * The published values and the word on screen together, so an operator searching `in_trip` and one
 * searching `in trip` find the same row.
 */
function searchableText(trip: Trip): string {
  return [
    trip.tripId,
    trip.riderId,
    trip.driverId,
    trip.assignmentId,
    trip.requestId,
    trip.status,
    readableStatus(trip.status),
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the order TRP-01 answered in — stalest first, which its contract states and gives a
 * reason for: "the trip that has gone longest without an update is the one a dispatcher is looking
 * for, and the ones that just changed are taking care of themselves". The screen must not quietly
 * replace it, so `default` reorders nothing.
 *
 * There is no state filter here. The design filters its four sample rows in the browser; over a
 * real listing that cannot work, because the unfiltered page is TRP-01's live set and the three
 * terminal statuses are not in it at any page. So the filter is a query parameter, handled by the
 * component's fetch, and this function sees only what came back.
 */
export function ridePage(rows: readonly Trip[], query: RideQuery): RidePage {
  const needle = query.search.trim().toLowerCase();
  const matched =
    needle.length === 0 ? [...rows] : rows.filter((trip) => searchableText(trip).includes(needle));

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return readableStatus(left.status).localeCompare(readableStatus(right.status));
          }
          // "Name" is the first column, as the design's A–Z is: the trip, not the rider.
          const compared = left.tripId.localeCompare(right.tripId);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/**
 * Two of the design's nine state classes tint a row, and TRP-01 can produce one of them.
 *
 * `no_show` is `failed`, so a rider who never came out tints its row. `human` — the design's fare
 * dispute — is not a trip status anywhere, so the violet tint is unreachable here.
 */
export function rowTint(status: TripStatus): string | undefined {
  return status === "no_show" ? "rides__row--failed" : undefined;
}

/**
 * The one sentence this board owes an operator about what it actually fetched, or nothing.
 *
 * The design gives the grid no such line, and the demand queue and the offer monitor both added one
 * for the reason R-08 defends: an operator working a partial board while believing it whole is the
 * failure it exists to prevent. Here it carries both facts, and the second matters because the
 * page's own description — the design's words, kept — says "All trips in progress and today's
 * finished ones", which is wider than what TRP-01 answers unasked.
 *
 * Truncation wins when both are true, because a partial page is the stronger claim: 200 of 400
 * trips read as all of them is worse than a default nobody has noticed the shape of.
 *
 * @param fetched how many rows came back, or `undefined` before anything has.
 * @param truncated whether TRP-01 answered a cursor, meaning this is the head of the list.
 * @param status the state the operator chose, or `undefined` for the service's own default.
 */
export function boundedNote(
  fetched: number | undefined,
  truncated: boolean,
  status: TripStatus | undefined,
  perFetch: number,
): string | undefined {
  if (fetched === undefined) return undefined;
  if (truncated) {
    return `More than ${perFetch} trips match. This board shows the first ${perFetch}, so the count is a floor rather than a total.`;
  }
  if (status === undefined) {
    return "All states is every trip still running. TRP-01 answers completed, cancelled and no-show trips only when one is chosen above, and it has no date filter, so today's finished trips cannot be asked for as such.";
  }
  return undefined;
}

/**
 * The selected trip, for the panel beside the board.
 *
 * **The four reference pairs are what this panel is for.** A trip is the join of an assignment, a
 * request and a quote, and TRP-01 publishes each identifier with the version it was joined at —
 * `assignmentId`/`assignmentVersion`, `requestId`/`requestVersion`, `quoteId`/`quoteVersion`. That
 * is how a dispatcher follows one ride back through DSP-03, MOB-03 and MOB-02, and the grid has
 * room for none of it. The versions travel with the identifiers rather than being dropped as
 * noise: a reference without the version it was taken at is a reference to whatever that record
 * says now, which is a different record.
 *
 * **The rider is an identifier and will stay one here.** R-19 measured it: nothing this console can
 * reach publishes a rider's name — CUS-01 is a customer profile the dispatch client does not hold a
 * scope for, and CUS-02 and CUS-03 answer saved places and preferences. The panel says so rather
 * than leaving the reader to wonder whether the name failed to load.
 *
 * **The driver is an identifier too, and for a different reason.** DRV-01 publishes a name and
 * this board does not read it — the roster and the offer monitor do. Joining it here would add a
 * second listing to a board that makes one request, which the bounded-fan-out suite counts; the
 * panel names the absence instead and says which board has it.
 *
 * **There is no route, no fare and no position**, and each absence belongs to a different service.
 * A trip knows who, when and in which state; where it is, is DRV-06's and the live map's, and what
 * it costs is MOB-02's and is not deployed.
 */
export function rideDetail(trip: Trip): RecordDetail {
  const classKey = classOfStatus("TRP-01", trip.status);

  return {
    headline: readableStatus(trip.status),
    identifier: trip.tripId,
    source: "TRP-01",
    ...(classKey === undefined
      ? {}
      : {
          state: {
            classKey,
            label: readableStatus(trip.status),
            code: "TRP-01",
            status: trip.status,
          },
        }),
    fields: [
      field("Driver reference", trip.driverId, true),
      field("Rider reference", trip.riderId, true),
      // Each reference with the version it was joined at; see this function's head.
      field("Assignment", `${trip.assignmentId} · v${String(trip.assignmentVersion)}`, true),
      field("Request", `${trip.requestId} · v${String(trip.requestVersion)}`, true),
      field("Quote", `${trip.quoteId} · v${String(trip.quoteVersion)}`, true),
      field("Started", at(trip.createdAt) ?? trip.createdAt),
      field("Last changed", at(trip.updatedAt) ?? trip.updatedAt),
      absent(
        "Rider name",
        "Nothing this console can reach publishes one. CUS-01 holds the profile and this client holds no scope for it; CUS-02 and CUS-03 answer saved places and preferences. R-19.",
      ),
      absent(
        "Driver name",
        "DRV-01 publishes one and this board does not read it. The driver roster and the offer monitor do, and joining it here would add a second listing to a board that makes one request.",
      ),
      absent(
        "Where it is",
        "DRV-06 publishes the driver's position and this board does not read it — the live map does.",
      ),
      /*
       * The fare, which this board recorded as absent until 21 October and which TRP-01 publishes
       * on the row.
       *
       * The old reason — "MOB-02 ride-quote owns the price and is not deployed" — was true of the
       * *quote* and was never true of the trip. TRP-01 carries `fare` on `Trip` itself, and its
       * contract says why it is recorded at creation rather than read back: neither MOB-02's read
       * nor the pinned `quoteVersion` can retrieve the version the trip agreed to, so a composition
       * would answer with the quote as it stands now, which is the wrong number for a history.
       *
       * It stays optional, because most trips genuinely have none: TRP-01's validator rejected
       * every fare it was ever offered until a lost backslash was fixed on the same day, so no trip
       * recorded before then carries one. `absent` is therefore wrong in both directions — the
       * contract does publish it — and so is a blank line. The field appears when the row has it.
       */
      ...(trip.fare === undefined
        ? [
            absent(
              "Fare",
              "TRP-01 publishes a fare on the trip and this one carries none. Its validator refused every fare it was offered until 21 October, so no trip recorded before then has one, and TRP-01 publishes no way to amend a trip.",
            ),
          ]
        : [field("Fare", `${trip.fare.currency} ${majorUnits(trip.fare.amountMinor)}`)]),
      field("Version", `v${String(trip.version)}`),
    ],
  };
}

/**
 * The one scope this board spends to act, and it was published by nothing until 21 October.
 *
 * TRP-01's `authorizeTransition` has always honoured `trip:workload:transition`, and the service's
 * own integration tests mint tokens carrying it — but no contract named it, so no client could be
 * granted it and this board could not call an operation its own design cites. The scope that *was*
 * published on that operation, `trip:driver:transition`, is the one of the three that can authorize
 * nothing: it requires the actor's identity subject to equal the trip's `driverId`, and R-29 is
 * about those being two different things on purpose.
 */
export const CANCEL_SCOPE = "trip:workload:transition";

/**
 * Whether a trip can still be cancelled, by the service's own rule rather than by this board's.
 *
 * TRP-01's transition table has no path out of `completed`, `cancelled` or `no_show`, so offering
 * the action on one of those would draw a control whose only outcome is a 409. The live set is the
 * same five `LIVE_STATUSES` names, which is not a coincidence — a trip that is still running is
 * exactly a trip that can still be called off.
 */
export function cancellable(status: TripStatus): boolean {
  return LIVE_STATUSES.includes(status);
}

/**
 * Cancel a trip on the rider's behalf.
 *
 * The design's own note on this board: *"Cancelling a trip for the rider asks for a reason and
 * shows the fee before you confirm."* The reason is `reasonCode`, which TRP-01 requires on a
 * cancellation and refuses the transition without. **The fee has no source** — nothing on this
 * platform publishes a cancellation fee, by any name, so the confirmation draws its absence rather
 * than a number it would have had to invent.
 *
 * `expectedVersion` is the version the row was read at, so two operators cancelling the same trip
 * from the same screen is a 409 rather than a second cancellation. The idempotency key is derived
 * from the trip and that version for the same reason: a retry of *this* cancellation replays, and a
 * cancellation of a trip that has since moved is a different command.
 */
export async function cancelTrip(
  transport: Transport,
  input: { readonly tenantId: string; readonly trip: Trip; readonly reasonCode: string },
): Promise<Trip> {
  const answer = await services.trip__trip.createTripClient(transport).postTenantsTripsTransitions(
    input.tenantId,
    input.trip.tripId,
    {
      toStatus: "cancelled",
      expectedVersion: input.trip.version,
      occurredAt: new Date().toISOString(),
      reasonCode: input.reasonCode,
    },
    { idempotencyKey: `cancel/${input.trip.tripId}/v${String(input.trip.version)}` },
  );
  return answer.trip;
}

/**
 * `118000` → `1,180.00`, by splitting the string rather than dividing a number.
 *
 * **TRP-01 publishes `amountMinor` as a string and says why**: these are summed across a rider's
 * history and a cost centre's spend, and a JSON number stops being exact above 9007199254740991.
 * So the finance console's `money()` cannot be reused here — it takes a `number`, and converting
 * reintroduces the exact imprecision the contract avoided. Splitting the decimal spelling needs no
 * arithmetic on the amount at all, which is what makes it safe at any size.
 *
 * Two minor digits, which is right for KES, USD and every currency this deployment prices in — and
 * **wrong for UGX and RWF**, which have none. The finance console reads the digit count from the
 * currency through `Intl` and this does not, because that helper is built around a number and
 * lifting it to a shared package is its own change. Recorded rather than guessed at: a trip priced
 * in UGX would read a hundredfold low here, and no tenant prices in UGX yet.
 */
export function majorUnits(amountMinor: string): string {
  if (!/^\d+$/.test(amountMinor)) return `${amountMinor} minor units`;
  const padded = amountMinor.padStart(3, "0");
  const major = padded.slice(0, padded.length - 2);
  const fraction = padded.slice(padded.length - 2);
  const grouped = major.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${grouped}.${fraction}`;
}
